import { isEn } from "i18next-config";
import moment from "moment";
import { PHASE_COLORS } from "constants/phaseColors";

export class BiocyclePhasesDto {
  static fromApi(data) {
    const cycles = data?.cycles;
    const cyclesPhases = cycles.flatMap((cycle) => cycle.phaseProperties);

    const allPhases = cyclesPhases.map((phase) => {
      const properties = phase.properties;
      return {
        id: properties.id,
        number: (properties.id || "").split(" ")[1],
        name: isEn ? phase.name?.nameEn : phase.name?.nameGr,
        startDate: properties.periodStart,
        endDate: properties.periodEnd,
        color: PHASE_COLORS[properties.id],
      };
    });

    const sorted = sortPhases(allPhases);
    return sorted;
  }
}

export class BiocyclePhasePopulatedDto {
  static fromApi(data) {
    const { properties, name, description, stresses, ...restData } = data;

    const phaseData = {
      ...restData,
      id: properties.id,
      name: isEn ? name?.nameEn : name?.nameGr,
      description: isEn
        ? description?.descriptionEn
        : description?.descriptionGr,
      startDate: properties.periodStart,
      endDate: properties.periodEnd,
      color: PHASE_COLORS[properties.id],
    };
    return {
      ...phaseData,
      stresses: PhaseStressesListDto.fromApi(stresses, phaseData),
    };
  }
}

export class BiocyclePhasePopulatedListDto {
  static fromApi(dataArray) {
    const array = dataArray?.map((data) =>
      BiocyclePhasePopulatedDto.fromApi(data)
    );
    const sorted = sortPhases(array);
    return sorted;
  }
}

class PhaseStressDto {
  static fromApi(stress, phase) {
    return {
      id: phase.uuid + stress.uuid,
      uuid: stress.uuid,
      phaseUuid: phase.uuid,
      phaseId: phase.id,
      startDate: phase.startDate,
      endDate: phase.endDate,
      name: isEn ? stress.name.nameEn : stress.name.nameGr,
      typeName: isEn
        ? stress.stressTypeName.nameEn
        : stress.stressTypeName.nameGr,
      description: isEn
        ? stress.description.descriptionEn
        : stress.description.descriptionGr,
      priority: stress.priority,
      mineralImbalances: stress.mineralImbalances,
      color: phase.color,
      pathogens: stress.pathogens,
      weeds: stress.weeds,
      insects: stress.insects,
      numberOfEnemies: numberOfEnemies(stress),
    };
  }
}

class PhaseStressesListDto {
  static fromApi(stresses, phase) {
    const stressesArray = stresses?.map((s) =>
      PhaseStressDto.fromApi(s, phase)
    );
    return sortStresses(stressesArray);
  }
}

const sortPhases = (phases) => {
  if (!Array.isArray(phases)) {
    return;
  }
  const items = [...phases];

  return items.sort((a, b) => {
    const startDiff = moment(a.startDate).diff(moment(b.endDate));

    // If start dates are equal, compare spans
    if (startDiff === 0) {
      const phaseDiff = a.id.split(" ")[1] - b.id.split(" ")[1];

      if (phaseDiff !== 0) {
        return phaseDiff;
      } else {
        return moment(a.endDate).diff(moment(b.endDate));
      }
    }

    return startDiff;
  });
};

const sortStresses = (stresses) => {
  if (!Array.isArray(stresses)) {
    return;
  }
  const items = [...stresses];

  return items.sort((a, b) => {
    const startDiff = b.priority - a.priority;

    // If start dates are equal, compare spans
    if (startDiff === 0) {
      return a.phaseId < b.phaseId ? -1 : 1;
    }

    return startDiff;
  });
};

const numberOfEnemies = (item) =>
  [
    ...(item.weeds || []),
    ...(item.insects || []),
    ...(item.pathogens || []),
    ...(item.mineralImbalances || []),
  ].length;
